import { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    Typography
} from "@mui/material";
import { AppId } from "../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectStructureNodeById } from "../device/structureSlice";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { createURSRequirement, URSRequirement } from "../urs/UrsSlice";
import { selectLoggedUser } from "../user/userSlice";
import { AiAssistant } from "./AiAssistant";
import {
    AiAssistantMessage,
    UrsRequirementProposal,
    UrsRequirementProposalData
} from "./aiAssistantSlice";

interface UrsRequirementsAssistantProps {
    documentId: number;
    sectionId: string;
    sectionName: string;
    structureId: AppId;
}

export const UrsRequirementsAssistant = ({
    documentId,
    sectionId,
    sectionName,
    structureId
}: UrsRequirementsAssistantProps) => {
    const dispatch = useAppDispatch();
    const userName = useAppSelector(selectLoggedUser);
    const structureName = useAppSelector(
        state => selectStructureNodeById(state, structureId)?.name
    );
    const baseUserName = (userName || "").trim();
    const shortUserName = baseUserName.includes("@")
        ? baseUserName.split("@")[0]
        : baseUserName;
    const displayUserName = (shortUserName || "Użytkowniku").toLocaleUpperCase("pl-PL");

    const handleAddRequirements = async (
        requirements: UrsRequirementProposal[]
    ) => {
        if (!sectionId) {
            dispatch(showError("error"));
            return;
        }

        try {
            for (const requirement of requirements) {
                const name = requirement.content.trim();

                const entity: URSRequirement = {
                    id: "",
                    code: "",
                    name,
                    name_orig: "",
                    description: "",
                    criticality: "E",
                    typeId: null,
                    ursId: String(documentId),
                    severity: 0,
                    frequency: 0,
                    detection: 0,
                    potentialDefect: "",
                    consequence: "",
                    reason: "",
                    comment: null,
                    deleteReason: null,
                    note: null,
                    testDefinitionId: null,
                    requirementId: null,
                    stage: "DQ",
                    group: null,
                    order: 0,
                    guid: "",
                    status: "",
                    sectionGuid: sectionId,
                    parentRequirementId: null,
                    numeration: null,
                    offerMessages: [],
                    childRequirements: [],
                    modifyDate: null,
                    jsonContent: null,
                    searcheableContent: null,
                };

                await dispatch(createURSRequirement(entity)).unwrap();
            }

            dispatch(showSuccess("Dodano wymagania."));
        } catch (error) {
            dispatch(showError("error"));
        }
    };

    return (
        <AiAssistant
            contextId={documentId}
            assistantCode="URS_REQUIREMENTS"
            assistantId="A6679CD3-CB42-4EC7-AECA-D3D44A3A0DE9"
            title="Vincent"
            launcherMode="inline"
            launcherLabel="Vincent"
            initialInstruction={`
                Pomagaj użytkownikowi tworzyć wymagania
                dla aktualnego dokumentu URS.
            `}
            renderWelcomeMessage={() => (
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word"
                    }}
                >
                    Witaj <strong>{displayUserName}</strong>! 
                    Widzę, że pracujesz nad dokumentem dla „{structureName || "nieznanej struktury"}”. 
                    Chętnie pomogę w jego przygotowaniu! Od czego zaczynamy?
                </Typography>
            )}
            renderMessageData={message => (
                <UrsRequirementProposals
                    message={message}
                    onAdd={handleAddRequirements}
                    targetSectionName={sectionName}
                />
            )}
        />
    );
};

interface UrsRequirementProposalsProps {
    message: AiAssistantMessage;
    targetSectionName: string;

    onAdd: (
        requirements: UrsRequirementProposal[]
    ) => Promise<void>;
}

const UrsRequirementProposals = ({
    message,
    onAdd,
    targetSectionName
}: UrsRequirementProposalsProps) => {
    const [selectedIndexes, setSelectedIndexes] =
        useState<number[]>([]);

    const [isAdding, setIsAdding] =
        useState(false);
    const [addedIndexes, setAddedIndexes] =
        useState<number[]>([]);

    if (
        message.contentType !==
        "URS_REQUIREMENT_PROPOSALS"
    ) {
        return null;
    }

    if (!isUrsRequirementProposalData(message.data)) {
        return null;
    }

    const requirements =
        message.data.requirements;

    if (requirements.length === 0) {
        return null;
    }

    const handleToggle = (index: number) => {
        if (addedIndexes.includes(index)) {
            return;
        }

        setSelectedIndexes(prev => {
            if (prev.includes(index)) {
                return prev.filter(x => x !== index);
            }

            return [...prev, index];
        });
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedIndexes([]);
            return;
        }

        setSelectedIndexes(availableIndexes);
    };

    const handleAdd = async () => {
        const selectedRequirements =
            requirements.filter(
                (_, index) =>
                    selectedIndexes.includes(index)
            );

        if (selectedRequirements.length === 0) {
            return;
        }

        setIsAdding(true);

        try {
            await onAdd(selectedRequirements);

            setAddedIndexes(prev => [
                ...prev,
                ...selectedIndexes
            ]);

            setSelectedIndexes([]);
        } finally {
            setIsAdding(false);
        }
    };

    const availableIndexes =
        requirements
            .map((_, index) => index)
            .filter(index =>
                !addedIndexes.includes(index)
            );

    const allSelected =
        availableIndexes.length > 0 &&
        availableIndexes.every(index =>
            selectedIndexes.includes(index)
        );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                >
                    {requirements.length} propozycji
                </Typography>

                <Button
                    size="small"
                    onClick={handleSelectAll}
                    sx={{
                        minWidth: 0,
                        textTransform: "none"
                    }}
                >
                    {allSelected
                        ? "Odznacz wszystkie"
                        : "Zaznacz wszystkie"}
                </Button>
            </Box>

            {requirements.map(
                (requirement, index) => {
                    const selected =
                        selectedIndexes.includes(index);

                    const added =
                        addedIndexes.includes(index);

                    return (
                        <Paper
                            key={index}
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 2,

                                opacity: added ? 0.65 : 1,

                                bgcolor:
                                    selected || added
                                        ? "action.selected"
                                        : "background.paper"
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    m: 0,
                                    width: "100%",
                                    alignItems: "flex-start"
                                }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selected || added}
                                        disabled={added}
                                        onChange={() =>
                                            handleToggle(index)
                                        }
                                    />
                                }
                                label={
                                    <Box
                                        sx={{
                                            pt: 0.5,
                                            pr: 0.5
                                        }}
                                    >
                                        {added && (
                                            <Typography
                                                variant="caption"
                                                color="success.main"
                                                sx={{ display: "block", mt: 0.5 }}
                                            >
                                                Dodano do dokumentu
                                            </Typography>
                                        )}
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mt: 0.5,
                                                lineHeight: 1.45
                                            }}
                                        >
                                            {requirement.content}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                    );
                }
            )}

            <Box sx={{ mt: 0.5 }}>
                <Button
                    variant="contained"
                    size="small"
                    disabled={
                        selectedIndexes.length === 0 ||
                        isAdding
                    }
                    onClick={() => void handleAdd()}
                >
                    Dodaj zaznaczone ({selectedIndexes.length})
                </Button>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        mt: 0.75,
                        lineHeight: 1.3,
                        whiteSpace: "normal",
                        wordBreak: "break-word"
                    }}
                >
                    Do grupy: {targetSectionName}
                </Typography>
            </Box>
        </Box>
    );
};

const isUrsRequirementProposalData = (
    data: unknown
): data is UrsRequirementProposalData => {
    if (
        data === null ||
        typeof data !== "object"
    ) {
        return false;
    }

    const value =
        data as Partial<UrsRequirementProposalData>;

    if (!Array.isArray(value.requirements)) {
        return false;
    }

    return value.requirements.every(requirement =>
        requirement !== null &&
        typeof requirement === "object" &&
        typeof requirement.content === "string"
    );
};